import HttpOperationError from 'es/app/business-logic/HttpOperationError';
import { useI18n } from 'es/core/hooks/useI18n';
import { FormikErrors, FormikProps, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { Intent } from '@blueprintjs/core';
import { Callout } from 'es/app/components/callout/Callout';
import { useDebouncedCallback } from 'use-debounce';

interface IProps<TFormValues = unknown> {
    form: FormikProps<TFormValues>;
    submitError: Error | string;
    showBeforeSubmit?: boolean;
}

const NO_ERRORS: Record<string, string> = {};

const ValidationErrors = <TFormValues extends unknown>(props: IProps<TFormValues>) => {
    const { l10n } = useI18n('app/components');

    const { form, submitError, showBeforeSubmit } = props;

    const flattenedErrors = flattenErrors();

    const errorKeys = Object.keys(flattenedErrors);
    const errorCount = errorKeys?.length ?? 0;

    const blockref = useScrollToErrorBlock();

    if (errorCount === 0) return null;

    return (
        <div ref={blockref}>
            <Callout intent={Intent.DANGER} title={l10n('forms.validation.heading')}>
                <ul>
                    {errorKeys.map(fieldName => (
                        <li key={fieldName}>{flattenedErrors[fieldName]}</li>
                    ))}
                </ul>
            </Callout>
        </div>
    );

    function flattenErrors() {
        if (form.isValid && !submitError) return NO_ERRORS;
        if (form.submitCount === 0 && !showBeforeSubmit) return NO_ERRORS;

        const result: Record<string, string> = {};

        flattenErrorsFromRoot(form.errors);

        if (submitError) {
            result['application'] = getErrorDetails(submitError);
        }

        return result;

        //////////

        function flattenErrorsFromRoot(errors: unknown, qualifiedRoot?: string) {
            if (errors === null || typeof errors !== 'object') return;
            if (Array.isArray(errors)) return;
            Object.keys(errors).forEach(fieldName => {
                const qualifiedPath = (qualifiedRoot && `${qualifiedRoot}_${fieldName}`) || fieldName;
                const fieldError = (errors as Record<string, string | object>)[fieldName];

                if (typeof fieldError === 'string') {
                    result[qualifiedPath] = fieldError;
                } else if (Array.isArray(fieldError)) {
                    fieldError.forEach((error, index) => {
                        if (!error) return;

                        flattenErrorsFromRoot(error, `${qualifiedPath}_${index}`);
                    });
                } else {
                    flattenErrorsFromRoot(fieldError, qualifiedPath);
                }
            });
        }
    }
};

export default ValidationErrors;

function getErrorDetails(error: Error | string) {
    if (error instanceof HttpOperationError) {
        const { details } = error;

        return !!details ? `${error.message}: ${error.details}` : error.message;
    }

    return error instanceof Error ? error.message : error?.toString();
}

function useScrollToErrorBlock() {
    const blockRef = useRef<HTMLDivElement | null>(null);
    const { errors, isSubmitting } = useFormikContext();
    const scrollToErrorBlock = useDebouncedCallback((errors: FormikErrors<unknown>) => {
        if (Object.keys(errors).length === 0) return;
        blockRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 200);

    useEffect(() => {
        if (!isSubmitting) return;
        scrollToErrorBlock(errors);
    }, [scrollToErrorBlock, errors, isSubmitting]);

    return blockRef;
}
